import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogContent
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { orange } from "@material-ui/core/colors";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContactPhone as ContactPhoneIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  GetApp as GetAppIcon
} from "@material-ui/icons";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import api from "../../services/api";
import { openPrintWindow } from "../../utils/printReport";
import { exportToExcel, exportToExcelMultiSheet } from "../../utils/exportToExcel";

const useStyles = makeStyles((theme) => ({
  "@global": {
    "@media print": {
      "html, body, #root": {
        height: "auto !important",
        overflow: "visible !important",
        width: "100% !important",
      },
      "*": {
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
      },
      ".MuiContainer-root": {
        maxWidth: "none !important",
        width: "100% !important",
        padding: "0 !important",
      },
      ".MuiGrid-container": {
        width: "100% !important",
        margin: "0 !important",
        alignItems: "flex-start !important",
      },
      ".MuiGrid-item": {
        paddingTop: "8px !important",
        paddingBottom: "8px !important",
      },
      ".MuiAccordion-root": {
        pageBreakInside: "avoid",
      },
      ".MuiAccordionSummary-root": {
        minHeight: "48px !important",
      },
      ".MuiAccordionSummary-content": {
        margin: "12px 0 !important",
      },
      ".MuiCollapse-container, .MuiCollapse-wrapper, .MuiCollapse-wrapperInner": {
        height: "auto !important",
        overflow: "visible !important",
      },
      ".MuiCollapse-hidden": {
        visibility: "visible !important",
      },
      ".recharts-responsive-container": {
        width: "100% !important",
        minWidth: "0 !important",
      },
      ".MuiTableCell-root": {
        padding: "6px 8px !important",
        fontSize: "11px !important",
      },
    },
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    "@media print": {
      paddingTop: 0,
      paddingBottom: 0,
      margin: 0,
      overflow: "visible !important",
    },
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    "@media print": {
      height: "auto",
      overflow: "visible !important",
      breakInside: "auto",
      pageBreakInside: "auto",
      marginBottom: theme.spacing(1),
      boxShadow: "none",
      border: "1px solid #ddd",
    },
  },
  card: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.boxticket.main : theme.palette.primary.main,
    color: "#fff",
    "@media print": {
      height: "auto",
      backgroundColor: "#f8f8f8 !important",
      color: "#111 !important",
      border: "1px solid #ddd",
      boxShadow: "none",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  cardIcon: {
    fontSize: 40,
    marginRight: theme.spacing(2),
  },
  cardValue: {
    fontSize: 32,
    fontWeight: "bold",
  },
  cardLabel: {
    fontSize: 14,
    opacity: 0.9,
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
  },
  chartTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  accordionHeader: {
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.boxticket.main : theme.palette.primary.main,
    color: "#fff",
  },
  tableContainer: {
    maxHeight: 400,
    marginTop: theme.spacing(2),
    "@media print": {
      maxHeight: "none",
      overflow: "visible !important",
    },
  },
  chipDanger: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  chipWarning: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  printActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
    "@media print": {
      display: "none",
    },
  },
  printHeader: {
    display: "none",
    "@media print": {
      display: "block",
      marginBottom: theme.spacing(2),
      borderBottom: "1px solid #ddd",
      paddingBottom: theme.spacing(1),
    },
  },
  printChartContainer: {
    width: "100%",
    height: 260,
    "@media print": {
      height: 145,
    },
  },
  printHalfWidth: {
    "@media print": {
      flexBasis: "50% !important",
      maxWidth: "50% !important",
    },
  },
  printLineFullWidth: {
    "@media print": {
      flexBasis: "100% !important",
      maxWidth: "100% !important",
    },
  },
  hideOnPrint: {
    "@media print": {
      display: "none !important",
    },
  },
  printBoardSection: {
    "@media print": {
      pageBreakInside: "auto",
      breakInside: "auto",
    },
  },
  cardClickable: {
    cursor: "pointer",
  },
  modalTableContainer: {
    maxHeight: 500,
  },
}));

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#FF6B9D",
  "#C71585",
  "#20B2AA"
];

const ClienteReport = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const inativosTint = tintStyle(isDark, orange, 50, 700);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [filterType, setFilterType] = useState(1); // 1: days, 2: date range
  const [period, setPeriod] = useState(30);
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days').format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalTitle, setDetailsModalTitle] = useState("");
  const [detailsModalType, setDetailsModalType] = useState("clientes");
  const [detailsModalRows, setDetailsModalRows] = useState([]);
  const [estadosModalOpen, setEstadosModalOpen] = useState(false);
  const [estadoSelecionado, setEstadoSelecionado] = useState("");
  const [clientesEstadoSelecionado, setClientesEstadoSelecionado] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (filterType === 1 && period > 0) {
        params.days = period;
      } else if (filterType === 2) {
        params.dateFrom = dateFrom;
        params.dateTo = dateTo;
      }

      console.log('=== FRONTEND DEBUG ===');
      console.log('Parâmetros enviados:', params);
      
      const { data } = await api.get("/dashboard/clientes", { params });
      
      console.log('Dados recebidos - clientesUltimaCompra:', data.clientesUltimaCompra);
      console.log('=== FIM FRONTEND DEBUG ===');
      
      setReportData(data);
    } catch (error) {
      console.error("Erro ao buscar dados do relatório:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    fetchReportData();
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Relatorio_Clientes_${moment().format("YYYYMMDD_HHmm")}`,
    onBeforePrint: async () => {
      setIsPrinting(true);
      await new Promise((resolve) => setTimeout(resolve, 350));
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 8mm;
      }
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `
  });

  const handleExportExcel = () => {
    const rd = reportData;
    exportToExcelMultiSheet(
      [
        {
          name: "Resumo",
          rows: [{
            "Total Clientes": rd.totalClientes,
            "Clientes Ativos": rd.clientesAtivos,
            Prospects: rd.clientesProspects,
            "Follow-ups Atrasados": rd.followUpsAtrasados,
            "Total Contatos": rd.totalContatos,
            "Com Contato": rd.clientesComContato,
            "Sem Contato": rd.clientesSemContato,
            "Follow-ups Pendentes": rd.followUpsPendentes,
            "Follow-ups Concluídos": rd.followUpsConcluidos,
          }],
        },
        {
          name: "Por Status",
          rows: (rd.clientesPorStatus || []).map((r) => ({
            Status: r.status || "Não definido",
            Total: r.total,
          })),
        },
        {
          name: "Por Estado",
          rows: (rd.clientesPorEstado || []).map((r) => ({
            Estado: r.estado || "Não definido",
            Total: r.total,
          })),
        },
        {
          name: "Por Segmento",
          rows: (rd.clientesPorSegmento || []).map((r) => ({
            Segmento: r.segmento || "Não definido",
            Total: r.total,
          })),
        },
        {
          name: "Por Responsável",
          rows: (rd.clientesPorResponsavel || []).map((r) => ({
            Responsável: r.usuario_nome,
            Total: r.total,
          })),
        },
        {
          name: "Por Tipo",
          rows: (rd.clientesPorTipo || []).map((r) => ({
            Tipo: r.tipo || "Não definido",
            Total: r.total,
          })),
        },
        {
          name: "Evolução Mensal",
          rows: (rd.evolucaoClientesMensal || []).map((r) => ({
            Mês: r.mes,
            "Novos Clientes": r.novos,
            "Total Acumulado": r.total_acumulado,
          })),
        },
        {
          name: "Inativos 120d",
          rows: (rd.clientesInativos120Dias || []).flatMap((board) =>
            (board.clientes || []).map((c) => ({
              Board: board.boardName,
              Cliente: c.clienteNome,
              Card: c.cardTitle,
              "Último Histórico": c.ultimoHistorico
                ? moment(c.ultimoHistorico).format("DD/MM/YYYY")
                : "—",
              "Dias sem Contato": c.diasSemContato,
            }))
          ),
        },
      ],
      `relatorio-clientes-${moment().format("YYYYMMDD")}`
    );
  };

  const handleDetailsPrint = () => {
    const isFollowup = detailsModalType === "followups";
    const headers = isFollowup
      ? ["Follow-up ID", "Cliente", "Data Prevista", "Status", "Responsável", "Observação"]
      : ["Cliente ID", "Cliente", "Status", "Situação", "Última Compra"];
    const rows = detailsModalRows.map(row =>
      isFollowup
        ? [row.followupId, `${row.clienteNome} (${row.clienteId})`,
           row.dataFollowup ? moment(row.dataFollowup).format("DD/MM/YYYY") : "Não informado",
           row.status || "PENDENTE", row.responsavel || "Sem responsável", row.observacao || "-"]
        : [row.clienteId, row.clienteNome, row.status, row.situacao,
           row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra"]
    );
    openPrintWindow(
      detailsModalTitle,
      `Total de registros: ${detailsModalRows.length} | Período: ${formatSelectedPeriod()} | Impresso em: ${moment().format("DD/MM/YYYY HH:mm")}`,
      headers,
      rows
    );
  };

  const handleEstadosPrint = () => {
    const rows = clientesEstadoSelecionado.map(row => [
      row.clienteId, row.clienteNome, row.status, row.situacao,
      row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra"
    ]);
    openPrintWindow(
      `Clientes do estado: ${estadoSelecionado}`,
      `Total: ${clientesEstadoSelecionado.length} | Período: ${formatSelectedPeriod()} | Impresso em: ${moment().format("DD/MM/YYYY HH:mm")}`,
      ["Cliente ID", "Cliente", "Status", "Situação", "Última Compra"],
      rows
    );
  };

  const formatSelectedPeriod = () => {
    if (filterType === 1) {
      return `Últimos ${period} dias`;
    }

    return `${moment(dateFrom).format("DD/MM/YYYY")} até ${moment(dateTo).format("DD/MM/YYYY")}`;
  };

  const estadosTop10ChartData = (reportData?.clientesPorEstado || []).slice(0, 10);
  const estadosFullChartData = reportData?.clientesPorEstado || [];
  const estadosDetalhesMap = reportData?.clientesPorEstadoDetalhes || [];
  const hasSegmentoData = (reportData?.clientesPorSegmento || []).length > 0;
  const customFieldReports = reportData?.clientesPorCamposPersonalizados || [];

  // Copilot (GPT-5.3-Codex) | 2026-03-29: Abre modal com os detalhes de cada card do topo.
  const openDetailsModal = (cardKey) => {
    const detalhes = reportData?.detalhesCards;

    if (!detalhes) {
      return;
    }

    const modalMap = {
      totalClientes: {
        title: "Base Total de Clientes",
        type: "clientes",
        rows: detalhes.totalClientes || [],
      },
      clientesAtivos: {
        title: "Clientes Ativos no Período",
        type: "clientes",
        rows: detalhes.clientesAtivos || [],
      },
      clientesProspects: {
        title: "Prospects Trabalhados",
        type: "clientes",
        rows: detalhes.clientesProspects || [],
      },
      followUpsAtrasados: {
        title: "Follow-ups Vencidos",
        type: "followups",
        rows: detalhes.followUpsAtrasados || [],
      },
    };

    const target = modalMap[cardKey];

    if (!target) {
      return;
    }

    setDetailsModalTitle(target.title);
    setDetailsModalType(target.type);
    setDetailsModalRows(target.rows);
    setDetailsModalOpen(true);
  };

  // Copilot (GPT-5.3-Codex) | 2026-03-29: Abre modal de estados e preseleciona o estado com maior volume.
  const openEstadosModal = () => {
    const primeiroEstado = estadosFullChartData[0]?.estado || "";
    const primeiroDetalhe = estadosDetalhesMap.find((item) => item.estado === primeiroEstado);

    setEstadoSelecionado(primeiroEstado);
    setClientesEstadoSelecionado(primeiroDetalhe?.clientes || []);
    setEstadosModalOpen(true);
  };

  // Copilot (GPT-5.3-Codex) | 2026-03-29: Clique na barra do grafico seleciona estado e exibe lista de clientes correspondente.
  const handleEstadoBarClick = (barData) => {
    const estado = barData?.estado || barData?.payload?.estado;

    if (!estado) {
      return;
    }

    const detalheEstado = estadosDetalhesMap.find((item) => item.estado === estado);
    setEstadoSelecionado(estado);
    setClientesEstadoSelecionado(detalheEstado?.clientes || []);
  };

  if (loading || !reportData) {
    return (
      <Container className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className={classes.container}>
      <div className={classes.printActions}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={handleExportExcel}
          style={{ marginLeft: 8 }}
        >
          Exportar Excel
        </Button>
      </div>

      <div ref={printRef}>
      <div className={classes.printHeader}>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          Relatório de Clientes
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Período: {formatSelectedPeriod()} | Impresso em: {moment().format("DD/MM/YYYY HH:mm")}
        </Typography>
      </div>

      {/* Filtros */}
      <Paper className={`${classes.paper} ${classes.hideOnPrint}`} style={{ marginBottom: 24 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Filtro</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value={1}>Por Período</MenuItem>
                <MenuItem value={2}>Por Data</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {filterType === 1 ? (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <MenuItem value={7}>Últimos 7 dias</MenuItem>
                  <MenuItem value={15}>Últimos 15 dias</MenuItem>
                  <MenuItem value={30}>Últimos 30 dias</MenuItem>
                  <MenuItem value={60}>Últimos 60 dias</MenuItem>
                  <MenuItem value={90}>Últimos 90 dias</MenuItem>
                  <MenuItem value={180}>Últimos 6 meses</MenuItem>
                  <MenuItem value={365}>Último ano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Data Inicial"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Data Final"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleFilterChange}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Cards de Resumo */}
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={`${classes.card} ${classes.cardClickable}`}
            onClick={() => openDetailsModal("totalClientes")}
          >
            <div className={classes.cardHeader}>
              <PeopleIcon className={classes.cardIcon} />
              <div>
                <Typography className={classes.cardValue}>
                  {reportData.totalClientes}
                </Typography>
                <Typography className={classes.cardLabel}>
                  Base Total de Clientes
                </Typography>
              </div>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={`${classes.card} ${classes.cardClickable}`}
            style={{ backgroundColor: "#4caf50" }}
            onClick={() => openDetailsModal("clientesAtivos")}
          >
            <div className={classes.cardHeader}>
              <CheckCircleIcon className={classes.cardIcon} />
              <div>
                <Typography className={classes.cardValue}>
                  {reportData.clientesAtivos}
                </Typography>
                <Typography className={classes.cardLabel}>
                  Clientes Ativos no Período
                </Typography>
              </div>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={`${classes.card} ${classes.cardClickable}`}
            style={{ backgroundColor: "#ff9800" }}
            onClick={() => openDetailsModal("clientesProspects")}
          >
            <div className={classes.cardHeader}>
              <TrendingUpIcon className={classes.cardIcon} />
              <div>
                <Typography className={classes.cardValue}>
                  {reportData.clientesProspects}
                </Typography>
                <Typography className={classes.cardLabel}>
                  Prospects Trabalhados
                </Typography>
              </div>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            className={`${classes.card} ${classes.cardClickable}`}
            style={{ backgroundColor: "#f44336" }}
            onClick={() => openDetailsModal("followUpsAtrasados")}
          >
            <div className={classes.cardHeader}>
              <WarningIcon className={classes.cardIcon} />
              <div>
                <Typography className={classes.cardValue}>
                  {reportData.followUpsAtrasados}
                </Typography>
                <Typography className={classes.cardLabel}>
                  Follow-ups Vencidos
                </Typography>
              </div>
            </div>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos - Linha 1 */}
      <Grid container spacing={3} style={{ marginTop: 16 }} alignItems="flex-start">
        <Grid item xs={12} md={6} className={classes.printHalfWidth}>
          <Paper className={classes.paper}>
            <Typography className={classes.chartTitle}>
              Situação Comercial no Período
            </Typography>
            <div className={classes.printChartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reportData.clientesPorStatus}
                  dataKey="total"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={isPrinting ? 58 : 90}
                  label
                  isAnimationActive={!isPrinting}
                >
                  {reportData.clientesPorStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} className={classes.printHalfWidth}>
          <Paper className={classes.paper}>
            <Typography className={classes.chartTitle}>
              Última Compra dos Clientes
            </Typography>
            <div className={classes.printChartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { periodo: "Últimos 30 dias", total: reportData.clientesUltimaCompra.ultimos30dias },
                  { periodo: "31-60 dias", total: reportData.clientesUltimaCompra.ultimos60dias },
                  { periodo: "61-90 dias", total: reportData.clientesUltimaCompra.ultimos90dias },
                  { periodo: "Mais de 90 dias", total: reportData.clientesUltimaCompra.maisde90dias },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill="#8884d8" isAnimationActive={!isPrinting} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos - Linha 2 */}
      <Grid container spacing={3} style={{ marginTop: 16 }} alignItems="flex-start">
        {hasSegmentoData && (
          <Grid item xs={12} md={6} className={classes.printHalfWidth}>
            <Paper className={classes.paper}>
              <Typography className={classes.chartTitle}>
                Clientes por Segmento
              </Typography>
              <div className={classes.printChartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.clientesPorSegmento}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segmento" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#00C49F" isAnimationActive={!isPrinting} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={hasSegmentoData ? 6 : 12} className={hasSegmentoData ? classes.printHalfWidth : classes.printLineFullWidth}>
          <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography className={classes.chartTitle} style={{ marginBottom: 0 }}>
                Clientes por Estado (Top 10)
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className={classes.hideOnPrint}
                onClick={openEstadosModal}
              >
                Ver todos os estados
              </Button>
            </Box>
            <div className={classes.printChartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={estadosTop10ChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="estado" type="category" width={50} />
                <Tooltip />
                <Bar dataKey="total" fill="#FFBB28" isAnimationActive={!isPrinting} />
              </BarChart>
            </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos - Linha 3 */}
      <Grid container spacing={3} style={{ marginTop: 16 }}>
        <Grid item xs={12} className={classes.printLineFullWidth}>
          <Paper className={classes.paper}>
            <Typography className={classes.chartTitle}>
              Evolução de Clientes nos Últimos 12 Meses
            </Typography>
            <div className={classes.printChartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={reportData.evolucaoClientesMensal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis yAxisId="left" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="novos"
                  stroke="#8884d8"
                  name="Novos Clientes"
                  strokeWidth={2}
                  isAnimationActive={!isPrinting}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_acumulado"
                  stroke="#82ca9d"
                  name="Total Acumulado"
                  strokeWidth={2}
                  isAnimationActive={!isPrinting}
                />
              </LineChart>
            </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Autor: GitHub Copilot | Data: 30/03/2026 | Motivo: renderizar graficos dinamicos de campos personalizados com regra de orientacao por quantidade de categorias. */}
      {customFieldReports.length > 0 && (
        <Grid container spacing={3} style={{ marginTop: 16 }} alignItems="flex-start">
          {customFieldReports.map((fieldReport, fieldIndex) => {
            const chartData = fieldReport.categorias || [];
            const isHorizontalChart = chartData.length <= 10;
            const customChartHeight = isHorizontalChart
              ? Math.max(chartData.length * 28, 260)
              : 260;

            return (
              <Grid item xs={12} md={6} className={classes.printHalfWidth} key={fieldReport.fieldName}>
                <Paper className={classes.paper}>
                  <Typography className={classes.chartTitle}>
                    {fieldReport.fieldLabel}
                  </Typography>
                  <div className={classes.printChartContainer} style={{ height: customChartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {isHorizontalChart ? (
                        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis dataKey="label" type="category" width={120} />
                          <Tooltip />
                          <Bar
                            dataKey="total"
                            fill="rgb(0, 136, 254)"
                            isAnimationActive={!isPrinting}
                          />
                        </BarChart>
                      ) : (
                        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            angle={-35}
                            textAnchor="end"
                            height={75}
                            interval={0}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar
                            dataKey="total"
                            fill="rgb(0, 136, 254)"
                            isAnimationActive={!isPrinting}
                          />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Cards de Informações Adicionais */}
      <Grid container spacing={3} style={{ marginTop: 16 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" mb={2}>
              <ContactPhoneIcon style={{ marginRight: 8, fontSize: 32 }} />
              <Typography variant="h6">Contatos</Typography>
            </Box>
            <Typography variant="h4">{reportData.totalContatos}</Typography>
            <Typography variant="body2" color="textSecondary">
              Total de contatos cadastrados
            </Typography>
            <Box mt={2}>
              <Typography variant="body2">
                Com contato: <strong>{reportData.clientesComContato}</strong>
              </Typography>
              <Typography variant="body2">
                Sem contato: <strong>{reportData.clientesSemContato}</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" mb={2}>
              <AssignmentIcon style={{ marginRight: 8, fontSize: 32 }} />
              <Typography variant="h6">Follow-ups</Typography>
            </Box>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Pendentes: <strong>{reportData.followUpsPendentes}</strong>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Concluídos: <strong>{reportData.followUpsConcluidos}</strong>
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4, color: '#f44336' }}>
              Atrasados: <strong>{reportData.followUpsAtrasados}</strong>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" mb={2}>
              <HistoryIcon style={{ marginRight: 8, fontSize: 32 }} />
              <Typography variant="h6">Histórico</Typography>
            </Box>
            <Typography variant="body2">
              {reportData.historicosPorTipo.length > 0 ? (
                reportData.historicosPorTipo.map((hist, idx) => (
                  <div key={idx} style={{ marginTop: 4 }}>
                    {hist.tipo}: <strong>{hist.total}</strong>
                  </div>
                ))
              ) : (
                "Nenhum histórico registrado"
              )}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.paper}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonAddIcon style={{ marginRight: 8, fontSize: 32 }} />
              <Typography variant="h6">Por Tipo</Typography>
            </Box>
            <Typography variant="body2">
              {reportData.clientesPorTipo.length > 0 ? (
                reportData.clientesPorTipo.map((tipo, idx) => (
                  <div key={idx} style={{ marginTop: 4 }}>
                    {tipo.tipo || "Não definido"}: <strong>{tipo.total}</strong>
                  </div>
                ))
              ) : (
                "Nenhum tipo definido"
              )}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Top 10 Responsáveis */}
      {reportData.clientesPorResponsavel.length > 0 && (
        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography className={classes.chartTitle}>
                Top 10 Responsáveis por Clientes
              </Typography>
              <div className={classes.printChartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.clientesPorResponsavel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="usuario_nome" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip 
                    formatter={(value, name, props) => {
                      if (name === "total") {
                        return [value, "Total de Clientes"];
                      }
                      return value;
                    }}
                    labelFormatter={(label) => {
                      const data = reportData.clientesPorResponsavel.find(
                        item => item.usuario_nome === label
                      );
                      return data ? `${data.usuario_nome || `ID: ${data.usuario_id}`}` : label;
                    }}
                  />
                  <Bar dataKey="total" fill="#8884d8" isAnimationActive={!isPrinting} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Clientes Inativos há mais de 120 dias */}
      {reportData.clientesInativos120Dias && reportData.clientesInativos120Dias.length > 0 && (
        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Box display="flex" alignItems="center" mb={2}>
                <ScheduleIcon style={{ marginRight: 8, fontSize: 32, color: "#f44336" }} />
                <Typography className={classes.chartTitle} style={{ marginBottom: 0 }}>
                  Clientes sem Contato há mais de 120 dias
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Clientes com cards criados mas sem registro de histórico de relacionamento nos últimos 120 dias, agrupados por funil de vendas.
              </Typography>

              {/* Resumo Total */}
              <Box mb={3}>
                <Card style={{ backgroundColor: inativosTint.backgroundColor, border: "1px solid #ff9800" }}>
                  <CardContent>
                    <Typography variant="h6" style={{ color: inativosTint.color }}>
                      Total de Clientes Inativos: {reportData.clientesInativos120Dias.reduce((acc, board) => acc + board.clientes.length, 0)}
                    </Typography>
                    <Typography variant="body2" style={{ color: inativosTint.color, marginTop: 4 }}>
                      Distribuídos em {reportData.clientesInativos120Dias.length} funil(is) de vendas
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Gráfico de Resumo por Funil */}
              <div className={classes.printChartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={reportData.clientesInativos120Dias.map(board => ({
                    funil: board.boardName,
                    total: board.clientes.length
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="funil" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#f44336" name="Clientes Inativos" isAnimationActive={!isPrinting} />
                </BarChart>
              </ResponsiveContainer>
              </div>

              {/* Detalhamento por Funil */}
              <Box mt={3}>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                  Detalhamento por Funil de Vendas
                </Typography>
                {isPrinting ? (
                  reportData.clientesInativos120Dias.map((board) => (
                    <Box key={board.boardId} className={classes.printBoardSection} mb={3}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{
                          backgroundColor: isDark ? theme.palette.action.hover : "#fafafa",
                          border: `1px solid ${isDark ? theme.palette.divider : "#ddd"}`,
                          padding: 10,
                        }}
                      >
                        <Typography variant="h6">{board.boardName}</Typography>
                        <Typography variant="body2" style={{ color: "#f44336", fontWeight: "bold" }}>
                          {board.clientes.length} cliente(s)
                        </Typography>
                      </Box>
                      <TableContainer style={{ marginTop: 8 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Cliente</strong></TableCell>
                              <TableCell><strong>Card</strong></TableCell>
                              <TableCell align="center"><strong>Último Contato</strong></TableCell>
                              <TableCell align="center"><strong>Dias sem Contato</strong></TableCell>
                              <TableCell align="center"><strong>Status</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {board.clientes.map((cliente) => (
                              <TableRow key={`${cliente.clienteId}-${cliente.cardId}`}>
                                <TableCell>
                                  <Typography variant="body2">
                                    <strong>{cliente.clienteNome}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    ID: {cliente.clienteId}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{cliente.cardTitle}</Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Card ID: {cliente.cardId}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {cliente.ultimoHistorico
                                    ? moment(cliente.ultimoHistorico).format("DD/MM/YYYY")
                                    : "Sem registro"}
                                </TableCell>
                                <TableCell align="center">
                                  {cliente.diasSemContato !== null
                                    ? `${cliente.diasSemContato} dias`
                                    : "N/A"}
                                </TableCell>
                                <TableCell align="center">
                                  {cliente.diasSemContato === null
                                    ? "Sem Histórico"
                                    : cliente.diasSemContato > 180
                                      ? "Crítico"
                                      : "Atenção"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))
                ) : (
                  reportData.clientesInativos120Dias.map((board, index) => (
                    <Accordion key={board.boardId} defaultExpanded={index === 0}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon style={{ color: "#fff" }} />}
                        className={classes.accordionHeader}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Typography variant="h6">
                            {board.boardName}
                          </Typography>
                          <Chip
                            label={`${board.clientes.length} cliente(s)`}
                            style={{ backgroundColor: "#fff", color: "#f44336", fontWeight: "bold", marginRight: 16 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer className={classes.tableContainer}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Cliente</strong></TableCell>
                                <TableCell><strong>Card</strong></TableCell>
                                <TableCell align="center"><strong>Último Contato</strong></TableCell>
                                <TableCell align="center"><strong>Dias sem Contato</strong></TableCell>
                                <TableCell align="center"><strong>Status</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {board.clientes.map((cliente) => (
                                <TableRow key={`${cliente.clienteId}-${cliente.cardId}`}>
                                  <TableCell>
                                    <Typography variant="body2">
                                      <strong>{cliente.clienteNome}</strong>
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      ID: {cliente.clienteId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {cliente.cardTitle}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      Card ID: {cliente.cardId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {cliente.ultimoHistorico
                                      ? moment(cliente.ultimoHistorico).format("DD/MM/YYYY")
                                      : "Sem registro"
                                    }
                                  </TableCell>
                                  <TableCell align="center">
                                    {cliente.diasSemContato !== null
                                      ? `${cliente.diasSemContato} dias`
                                      : "N/A"
                                    }
                                  </TableCell>
                                  <TableCell align="center">
                                    {cliente.diasSemContato === null ? (
                                      <Chip
                                        label="Sem Histórico"
                                        className={classes.chipDanger}
                                        size="small"
                                      />
                                    ) : cliente.diasSemContato > 180 ? (
                                      <Chip
                                        label="Crítico"
                                        className={classes.chipDanger}
                                        size="small"
                                      />
                                    ) : (
                                      <Chip
                                        label="Atenção"
                                        className={classes.chipWarning}
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box p={2} pb={0} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{detailsModalTitle}</Typography>
          <Typography variant="body2" color="textSecondary">
            Total de registros: {detailsModalRows.length}
          </Typography>
        </Box>
        <DialogContent>
          <TableContainer className={classes.modalTableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {detailsModalType === "followups" ? (
                    <>
                      <TableCell><strong>Follow-up ID</strong></TableCell>
                      <TableCell><strong>Cliente</strong></TableCell>
                      <TableCell><strong>Data Prevista</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Responsável</strong></TableCell>
                      <TableCell><strong>Observação</strong></TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell><strong>Cliente ID</strong></TableCell>
                      <TableCell><strong>Cliente</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Situação</strong></TableCell>
                      <TableCell><strong>Última Compra</strong></TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {detailsModalRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={detailsModalType === "followups" ? 6 : 5} align="center">
                      Nenhum registro encontrado para este card.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailsModalRows.map((row, index) => (
                    <TableRow key={`${detailsModalType}-${index}`}>
                      {detailsModalType === "followups" ? (
                        <>
                          <TableCell>{row.followupId}</TableCell>
                          <TableCell>{row.clienteNome} ({row.clienteId})</TableCell>
                          <TableCell>
                            {row.dataFollowup ? moment(row.dataFollowup).format("DD/MM/YYYY") : "Não informado"}
                          </TableCell>
                          <TableCell>{row.status || "PENDENTE"}</TableCell>
                          <TableCell>{row.responsavel || "Sem responsável"}</TableCell>
                          <TableCell>{row.observacao || "-"}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{row.clienteId}</TableCell>
                          <TableCell>{row.clienteNome}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell>{row.situacao}</TableCell>
                          <TableCell>
                            {row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra"}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <Box p={2} pt={0} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => {
              const isFollowup = detailsModalType === "followups";
              const rows = detailsModalRows.map((row) =>
                isFollowup
                  ? {
                      "Follow-up ID": row.followupId,
                      Cliente: `${row.clienteNome} (${row.clienteId})`,
                      "Data Prevista": row.dataFollowup ? moment(row.dataFollowup).format("DD/MM/YYYY") : "Não informado",
                      Status: row.status || "PENDENTE",
                      Responsável: row.responsavel || "Sem responsável",
                      Observação: row.observacao || "-",
                    }
                  : {
                      "Cliente ID": row.clienteId,
                      Cliente: row.clienteNome,
                      Status: row.status,
                      Situação: row.situacao,
                      "Última Compra": row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra",
                    }
              );
              exportToExcel(rows, detailsModalTitle || "relatorio", "Dados");
            }}
          >
            Exportar Excel
          </Button>
          <Button onClick={handleDetailsPrint} color="primary" variant="contained" startIcon={<PrintIcon />}>
            Imprimir
          </Button>
          <Button onClick={() => setDetailsModalOpen(false)} color="primary" variant="outlined">
            Fechar
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={estadosModalOpen}
        onClose={() => setEstadosModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box p={2} pb={0} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Clientes por Estado (Todos)</Typography>
          <Typography variant="body2" color="textSecondary">
            Clique em uma barra para ver os clientes do estado
          </Typography>
        </Box>
        <DialogContent>
          <Box style={{ height: Math.max(estadosFullChartData.length * 30, 320), maxHeight: 560 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={estadosFullChartData} layout="vertical" margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="estado" type="category" width={80} />
                <Tooltip />
                <Bar
                  dataKey="total"
                  fill="#FFBB28"
                  isAnimationActive={false}
                  cursor="pointer"
                  onClick={handleEstadoBarClick}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Box mt={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                {estadoSelecionado ? `Clientes do estado ${estadoSelecionado}` : "Selecione um estado no gráfico"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total: {clientesEstadoSelecionado.length}
              </Typography>
            </Box>

            <TableContainer className={classes.modalTableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Cliente ID</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Situação</strong></TableCell>
                    <TableCell><strong>Última Compra</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientesEstadoSelecionado.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Nenhum cliente encontrado para o estado selecionado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clientesEstadoSelecionado.map((row, index) => (
                      <TableRow key={`estado-cliente-${index}`}>
                        <TableCell>{row.clienteId}</TableCell>
                        <TableCell>{row.clienteNome}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.situacao}</TableCell>
                        <TableCell>
                          {row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <Box p={2} pt={0} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          {clientesEstadoSelecionado.length > 0 && (
            <Button
              color="primary"
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={() => {
                const rows = clientesEstadoSelecionado.map((row) => ({
                  "Cliente ID": row.clienteId,
                  Cliente: row.clienteNome,
                  Status: row.status,
                  Situação: row.situacao,
                  "Última Compra": row.ultimaCompra ? moment(row.ultimaCompra).format("DD/MM/YYYY") : "Sem compra",
                }));
                exportToExcel(rows, `clientes-estado-${estadoSelecionado || "todos"}`, estadoSelecionado || "Clientes");
              }}
            >
              Exportar Excel
            </Button>
          )}
          <Button onClick={handleEstadosPrint} color="primary" variant="contained" startIcon={<PrintIcon />}>
            Imprimir
          </Button>
          <Button onClick={() => setEstadosModalOpen(false)} color="primary" variant="outlined">
            Fechar
          </Button>
        </Box>
      </Dialog>
      </div>
    </Container>
  );
};

export default ClienteReport;
